import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Leaf } from "lucide-react";

const HERO_IMG = "/images/hero.webp";

const lines = ["Non aver paura", "di accendere", "la tua vita."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 1, delay: 0.35 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end overflow-hidden"
    >
      <motion.div
        style={{ y, willChange: "transform" }}
        className="absolute inset-0 -z-10 scale-110"
      >
        <img
          src={HERO_IMG}
          alt="Campi verdi e pale eoliche al tramonto"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-sage-50 via-sage-50/30 to-sage-50/10" />
        <div className="absolute inset-0 bg-sage-50/25" />
      </motion.div>

      <motion.div
        style={{ y: overlayY }}
        className="relative w-full max-w-7xl mx-auto px-6 lg:px-10 pb-20 lg:pb-28"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="inline-flex items-center gap-2 rounded-full border border-sage-300/70 bg-white/50 backdrop-blur px-4 py-1.5 mb-8"
        >
          <Leaf className="h-3.5 w-3.5 text-sage-600" strokeWidth={1.8} />
          <span className="text-xs uppercase tracking-[0.2em] font-medium text-forest-700">
            Consulenza luce &amp; gas
          </span>
        </motion.div>

        <h1
          data-testid="hero-title"
          className="font-serif text-forest-900 tracking-tight leading-[0.92] text-5xl sm:text-6xl lg:text-8xl max-w-5xl"
        >
          {lines.map((line, i) => (
            <span key={i} className="reveal-line">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row sm:items-end gap-8 sm:gap-14"
        >
          <p className="max-w-md text-lg font-light text-forest-700 leading-relaxed">
            Offerte su misura, costi trasparenti e assistenza continua. Carica la
            tua bolletta e ricevi una valutazione senza impegno.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => scrollTo("#contatti")}
              data-testid="hero-cta-primary"
              className="inline-flex items-center rounded-full bg-forest-900 px-7 py-4 text-sm font-medium text-sage-50 hover:bg-forest-700 transition-[background-color,transform] duration-300 ease-out hover:-translate-y-0.5 active:translate-y-0"
            >
              Valuta la bolletta
            </button>
            <button
              onClick={() => scrollTo("#servizi")}
              data-testid="hero-cta-secondary"
              className="inline-flex items-center rounded-full bg-sage-200 px-7 py-4 text-sm font-medium text-forest-900 hover:bg-sage-300 transition-[background-color,transform] duration-300 ease-out hover:-translate-y-0.5 active:translate-y-0"
            >
              I nostri servizi
            </button>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-6 lg:right-10 hidden sm:flex items-center gap-2 text-forest-700"
      >
        <ArrowDown className="h-4 w-4 animate-bounce" strokeWidth={1.6} />
        <span className="text-xs uppercase tracking-[0.2em]">Scorri</span>
      </motion.div>
    </section>
  );
}
